import React, { useState, useEffect } from 'react'
import SyntaxLensWidget from './SyntaxLensWidget'
import NotebookView from './NotebookView'
import { useIsMobile } from '../hooks/useIsMobile'

interface ZenViewerSimpleProps {
  sourceLanguage: string
  targetLanguage: string
  htmlContent: string
  className?: string
}

/**
 * ZenViewerSimple - Read-only viewer for shared SyntaxLens snippets
 * Clean, minimal interface with interactive lens and notebook view
 */
const ZenViewerSimple: React.FC<ZenViewerSimpleProps> = ({
  sourceLanguage,
  targetLanguage,
  htmlContent,
  className = ''
}) => {
  const isMobile = useIsMobile()
  const [showHoverHint, setShowHoverHint] = useState(false)
  // Default to notebook view on mobile (hover doesn't work on touch devices)
  const [activeTab, setActiveTab] = useState<'lens' | 'notebook'>(isMobile ? 'notebook' : 'lens')

  // Trigger animation on mount when content is available
  useEffect(() => {
    if (htmlContent) {
      setShowHoverHint(true)
    }
  }, [htmlContent])

  // Animate hover hint from orange to gray
  useEffect(() => {
    if (showHoverHint) {
      const timer = setTimeout(() => {
        setShowHoverHint(false)
      }, 3000) // Hold orange for 3 seconds, then fade to gray
      return () => clearTimeout(timer)
    }
  }, [showHoverHint])

  // Auto-switch to notebook view when viewport changes to mobile
  useEffect(() => {
    if (isMobile && activeTab === 'lens') {
      setActiveTab('notebook')
    }
  }, [isMobile, activeTab])
  return (
    <div className={`zen-viewer-simple h-screen bg-gray-50 overflow-hidden ${className}`}>
      {/* Header with Tab Navigation - Mobile Optimized */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-3 sm:px-6 py-2">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            {/* Left Side - Logo & Title (compact) - Clickable to home */}
            <div className="flex items-center gap-2 sm:gap-6">
              <button
                onClick={() => {
                  // Check if there's a return URL (e.g., from SharesAdmin)
                  const returnUrl = localStorage.getItem('syntaxlens_return_url')

                  if (returnUrl) {
                    // Clear it and navigate back
                    localStorage.removeItem('syntaxlens_return_url')
                    window.location.href = returnUrl
                  } else {
                    // Default: navigate to home
                    window.location.href = '/'
                  }
                }}
                className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity duration-200"
                title="Go back"
              >
                <svg className="w-6 h-6 sm:w-7 sm:h-7" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 28 H16 Q14 28 14 30 V70 Q14 72 16 72 H20" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                  <path d="M80 28 H84 Q86 28 86 30 V70 Q86 72 84 72 H80" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                  <polygon points="50,32 69,51 50,70 31,51" fill="none" stroke="black" strokeWidth="5.5"/>
                  <circle cx="50" cy="50" r="5.2" fill="black"/>
                </svg>
                <div className="hidden xs:block sm:block">
                  <div className="text-left">
                    <h1 className="text-sm sm:text-base font-bold text-gray-900">SyntaxLens</h1>
                  </div>
                </div>
              </button>

              {/* Tab Navigation - Hide Interactive tab on mobile (hover doesn't work) */}
              <div className="flex space-x-0.5 sm:space-x-1">
                {!isMobile && (
                  <button
                    onClick={() => setActiveTab('lens')}
                    className={`py-1.5 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm font-medium transition-all border-b-2 ${
                      activeTab === 'lens'
                        ? 'text-gray-900 border-gray-900'
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                    title="Interactive hover insights"
                  >
                    Interactive
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('notebook')}
                  className={`py-1.5 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm font-medium transition-all border-b-2 ${
                    activeTab === 'notebook'
                      ? 'text-gray-900 border-gray-900'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                  title="Notebook-style sequential breakdown"
                >
                  Notebook
                </button>
              </div>
            </div>

            {/* Right Side - Language Pair - Compact on mobile */}
            <div className="text-xs sm:text-sm font-medium text-gray-500 flex-shrink-0">
              <span className="hidden sm:inline">[{sourceLanguage}]</span>
              <span className="sm:hidden">{sourceLanguage}</span>
              <span className="mx-1 sm:mx-2">→</span>
              <span>{targetLanguage}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content - Centered with margin - Mobile Optimized */}
      <div className="h-[calc(100vh-52px)] overflow-auto">
        {activeTab === 'lens' ? (
          <div className="px-4 sm:px-8 md:px-16 lg:px-24">
            <SyntaxLensWidget
              className="h-full"
              sourceLanguage={sourceLanguage}
              targetLanguage={targetLanguage}
            >
              <div className="p-4 sm:p-8 bg-gray-900">
                <div className="mb-3 sm:mb-4 text-center">
                  <p
                    className={`text-xs sm:text-sm font-medium mb-2 transition-colors duration-1000 ${
                      showHoverHint ? 'text-orange-400' : 'text-gray-400'
                    }`}
                  >
                    ✨ Hover over any highlighted element below
                  </p>
                  <p className="text-gray-400 text-xs">⌨️ Navigate: ← → • Depth: 1 2 3 • Pin: Space • Exit: Esc</p>
                </div>
                <div className="font-mono text-xs sm:text-sm space-y-2 text-left">
                  <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                </div>
              </div>
            </SyntaxLensWidget>
          </div>
        ) : (
          <NotebookView
            html={htmlContent}
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
          />
        )}
      </div>
    </div>
  )
}

export default ZenViewerSimple
